import { useCallback, useEffect, useRef, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import { CommandPalette } from '@/components/layout/CommandPalette'
import { ShortcutsDialog } from '@/components/layout/ShortcutsDialog'
import { Sidebar, type SidebarMode } from '@/components/layout/Sidebar'
import { Topbar } from '@/components/layout/Topbar'
import { PageActionsSlotContext } from '@/components/layout/page-actions'
import { PageHeaderContext, type PageHeader } from '@/components/layout/page-header'
import { titleForPath } from '@/components/layout/nav-config'
import { useGlobalShortcuts } from '@/lib/keyboard/use-global-shortcuts'
import { useMediaQuery } from '@/lib/ui/use-media-query'
import { useOrganization } from '@/lib/org/use-organization'

/**
 * The application shell: sidebar, topbar, routed content.
 *
 * Three layouts, one component tree — the sidebar is expanded at ≥1024px, an
 * icon rail from 768px, and an overlay drawer below that. Only the content area
 * scrolls with the page; the sidebar keeps its own scroll position.
 *
 * The expanded rail starts at 1024 rather than 1280 because the rail cannot NAME
 * the company: it is 72px wide, so all it shows is a one-letter tile. On a
 * 1000-1280px laptop — an ordinary window, not a corner case — that left the one
 * fact an accounting app must never be ambiguous about readable only as "T".
 * 1024px leaves 768px of content beside a 256px sidebar, which is a phone's worth
 * of width for the actual work; below that the rail is the honest trade.
 */
export function AppShell() {
  const location = useLocation()
  const navigate = useNavigate()

  const isCompact = useMediaQuery('(max-width: 767px)')
  const isRail = useMediaQuery('(min-width: 768px) and (max-width: 1023px)')
  const mode: SidebarMode = isCompact ? 'drawer' : isRail ? 'rail' : 'expanded'
  const { organization } = useOrganization()

  // The topbar's action slot — pages portal their header buttons into it.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null)
  // A child screen may override the topbar title and add a back button.
  const [pageHeader, setPageHeader] = useState<PageHeader | null>(null)

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [shortcutsOpen, setShortcutsOpen] = useState(false)

  const menuButtonRef = useRef<HTMLButtonElement>(null)

  // A drawer left open behind a new page would cover it; and growing past the
  // breakpoint must not leave a stale overlay in the tree.
  useEffect(() => setDrawerOpen(false), [location.pathname])
  useEffect(() => {
    if (!isCompact) setDrawerOpen(false)
  }, [isCompact])

  // Closing the drawer must put focus back on the hamburger. The focus trap
  // handles the Esc case; a backdrop click leaves focus on <body>, so it is
  // restored explicitly here.
  const setDrawer = useCallback((open: boolean) => {
    setDrawerOpen(open)
    if (!open) menuButtonRef.current?.focus()
  }, [])

  useGlobalShortcuts({
    // `/` and the header search box both open the command palette — the one
    // real search surface — rather than focusing a separate, dead input.
    onFocusSearch: () => {
      setShortcutsOpen(false)
      setPaletteOpen(true)
    },
    onOpenCommandPalette: () => {
      setShortcutsOpen(false)
      setPaletteOpen(true)
    },
    onOpenShortcuts: () => {
      setPaletteOpen(false)
      setShortcutsOpen(true)
    },
    onGoTo: (path) => navigate(path),
  })

  return (
    <div className="flex h-dvh overflow-hidden bg-canvas text-fg">
      {/* First focusable element on the page, per docs/00-roadmap.md Part 3. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-60 focus:rounded-control focus:bg-accent focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-fg-on-accent"
      >
        Skip to content
      </a>

      <Sidebar mode={mode} open={drawerOpen} onOpenChange={setDrawer} />

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          title={pageHeader?.title ?? titleForPath(location.pathname)}
          backTo={pageHeader?.backTo ?? null}
          onBack={pageHeader?.backTo ? () => navigate(pageHeader.backTo!) : undefined}
          onOpenSearch={() => setPaletteOpen(true)}
          showMenuButton={mode === 'drawer'}
          onOpenMenu={() => setDrawerOpen(true)}
          menuButtonRef={menuButtonRef}
          // Which company you are posting into is not optional information. The
          // sidebar carries it when expanded; in the rail and the drawer it is a
          // one-letter tile or hidden behind a hamburger, so the topbar says it.
          {...(mode === 'expanded' ? {} : { orgName: organization?.name })}
        />

        {/* THE PAGE'S OWN ACTIONS, AT EVERY WIDTH.
            
            They used to live in the topbar, which is a FIXED 64px row that also
            holds the title, the company, search, refresh, theme and the account
            menu. Whether a period filter plus four buttons fitted depended on the
            page, the title length and the company name — so the failure mode was
            a control cropped or scrolled out of reach, and it moved around as you
            resized. A row of its own always has room and always wraps, which is
            worth 44px of height on the widest screens.

            `:empty` collapses it entirely on pages that portal nothing into it.
            `overflow-x-auto` is only the backstop for one control wider than the
            whole screen. */}
        <div
          ref={setActionsSlot}
          className="flex shrink-0 flex-wrap items-center gap-2 overflow-x-auto border-b border-border bg-surface px-3 py-2 empty:hidden sm:px-4"
        />

        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto bg-canvas outline-none"
        >
          <PageActionsSlotContext value={actionsSlot}>
            <PageHeaderContext value={setPageHeader}>
              <Outlet />
            </PageHeaderContext>
          </PageActionsSlotContext>
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsDialog open={shortcutsOpen} onOpenChange={setShortcutsOpen} />
    </div>
  )
}

export default AppShell
